// LT-349: API client for the LT-348 Campaign endpoints.
// Mirrors backend/routers/campaigns.py.
import axios from 'axios';

import type {
  Campaign,
  CampaignAuditLogEntry,
  CampaignCreatePayload,
  CampaignListParams,
  CampaignTransitionRequest,
  CampaignTransitionTarget,
  CampaignUpdatePayload,
} from '../types/campaign';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const campaignsAPI = {
  list: async (params?: CampaignListParams): Promise<Campaign[]> => {
    const r = await client.get<Campaign[]>('/campaigns', { params });
    return r.data;
  },

  get: async (id: string): Promise<Campaign> => {
    const r = await client.get<Campaign>(`/campaigns/${id}`);
    return r.data;
  },

  create: async (payload: CampaignCreatePayload): Promise<Campaign> => {
    // LT-548: the backend requires `creation_source`. Default to 'wizard'
    // (the legacy path) when a caller omits it so existing wizard call
    // sites keep working; LT-549/554 pass it explicitly per path.
    const body: CampaignCreatePayload = {
      creation_source: 'wizard',
      ...payload,
    };
    const r = await client.post<Campaign>('/campaigns', body);
    return r.data;
  },

  // LT-457: duplicate a campaign regardless of status. The source is
  // unchanged; the response is a fresh `draft` with all content copied.
  clone: async (id: string): Promise<Campaign> => {
    const r = await client.post<Campaign>(`/campaigns/${id}/clone`);
    return r.data;
  },

  update: async (id: string, patch: CampaignUpdatePayload): Promise<Campaign> => {
    const r = await client.put<Campaign>(`/campaigns/${id}`, patch);
    return r.data;
  },

  transition: async (
    id: string,
    target: CampaignTransitionTarget,
    body?: CampaignTransitionRequest,
  ): Promise<Campaign> => {
    const r = await client.post<Campaign>(
      `/campaigns/${id}/transitions/${target}`,
      body ?? {},
    );
    return r.data;
  },

  auditLog: async (
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<CampaignAuditLogEntry[]> => {
    const r = await client.get<CampaignAuditLogEntry[]>(
      `/campaigns/${id}/audit`,
      { params },
    );
    return r.data;
  },

  // LT-352: live KPI progress.
  kpis: async (id: string): Promise<{ kpis: CampaignKpiProgress[] }> => {
    const r = await client.get<{ kpis: CampaignKpiProgress[] }>(
      `/campaigns/${id}/kpis`,
    );
    return r.data;
  },

  // LT-354: per-(contact, campaign) enrolments. `exit_status` ∈ enum or
  // `"active"` for rows with NULL exit_status.
  enrolments: async (id: string, exit_status?: string): Promise<EnrolmentRow[]> => {
    const r = await client.get<EnrolmentRow[]>(
      `/campaigns/${id}/enrolments`,
      { params: exit_status ? { exit_status } : {} },
    );
    return r.data;
  },

  enrol: async (
    id: string,
    body: {
      contact_id: string;
      exit_reason?: string;
      // LT-356 override path: when the backend returns 409
      // {error: "already_enrolled", override_required: true}, retry
      // with these set to bypass the prior-enrolment block.
      force_re_enroll?: boolean;
      override_reason?: string;
    },
  ): Promise<EnrolmentRow> => {
    const r = await client.post<EnrolmentRow>(`/campaigns/${id}/enrolments`, body);
    return r.data;
  },

  exitEnrolment: async (
    id: string,
    enrolmentId: string,
    body: {
      exit_status: string;
      exit_reason?: string;
      deal_id?: string;
      manual_override?: boolean;
    },
  ): Promise<EnrolmentRow> => {
    const r = await client.post<EnrolmentRow>(
      `/campaigns/${id}/enrolments/${enrolmentId}/exit`,
      body,
    );
    return r.data;
  },

  // LT-357: dashboard report payload.
  report: async (id: string): Promise<CampaignReportPayload> => {
    const r = await client.get<CampaignReportPayload>(`/campaigns/${id}/report`);
    return r.data;
  },

  // LT-627: full call-detail CSV export for a campaign (active or
  // completed). Returns the CSVExportResponse envelope; the file is then
  // pulled from /exports/download/{export_id} (same flow the run CSV
  // export uses) via `downloadCampaignCsv` below.
  exportCsv: async (
    id: string,
    body?: { include_transcript?: boolean },
  ): Promise<CampaignCSVExportResponse> => {
    const r = await client.post<CampaignCSVExportResponse>(
      `/campaigns/${id}/export-csv`,
      body ?? {},
    );
    return r.data;
  },

  // Fetch the generated CSV as a blob and trigger a browser download.
  // Mirrors the run-export idiom in useRunDetailsPage.handleExportCallSummaries:
  // POST to mint the export, then GET the file with auth and click-download it.
  downloadCsv: async (
    id: string,
    body?: { include_transcript?: boolean },
  ): Promise<void> => {
    const meta = await campaignsAPI.exportCsv(id, body);
    if (!meta.success) {
      throw new Error(meta.message || 'Campaign CSV export failed.');
    }
    // The router serves the file at /exports/download/{export_id}; the
    // download_url field carries an /api-prefixed form that doesn't match
    // this client's origin baseURL, so build the path from export_id (same
    // way the run export builds its own download path).
    const fileResponse = await client.get(`/exports/download/${meta.export_id}`, {
      responseType: 'blob',
    });
    const blob = new Blob([fileResponse.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = meta.filename || `campaign_${id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // LT-643: the persisted campaign completion report (status + frozen
  // metrics + LLM narrative). `status: 'none'` until the campaign is terminal.
  completionReport: async (id: string): Promise<CompletionReportPayload> => {
    const r = await client.get<CompletionReportPayload>(
      `/campaigns/${id}/completion-report`,
    );
    return r.data;
  },

  // LT-643: (re)generate the report inline — only valid once the campaign is
  // terminal. Awaits metrics + narrative generation server-side.
  regenerateCompletionReport: async (id: string): Promise<CompletionReportPayload> => {
    const r = await client.post<CompletionReportPayload>(
      `/campaigns/${id}/completion-report/regenerate`,
    );
    return r.data;
  },
};

export interface CampaignReportActive {
  total_enrolments: number;
  active_count: number;
  by_exit_status: Record<string, number>;
  qualified_count: number;
  conversion_rate_pct: number;
  pipeline_dollars: number;
}

export interface CampaignReportPostCompletion {
  deal_count: number;
  pipeline_dollars: number;
  has_data: boolean;
}

export interface CampaignReportPayload {
  active: CampaignReportActive;
  post_completion: CampaignReportPostCompletion;
  generated_at: string;
}

// LT-643 — campaign completion report contract (mirrors
// services/campaign_completion_report.serialize_completion_report).
export interface CompletionReportCampaign {
  id: string;
  name: string;
  status: string;
  end_reason?: string | null;
  purpose?: string | null;
  target_audience?: string | null;
  campaign_goals?: string | null;
  launched_at?: string | null;
  ended_at?: string | null;
  created_at?: string | null;
  generated_at: string;
}

export interface CompletionReportKpi {
  name: string;
  target_value: number;
  unit: string;
  current_value: number;
  computed: boolean;
  attained: boolean | null;
}

export interface CompletionReportCapturedField {
  key: string;
  label: string;
  type: string;
  rollup: Record<string, unknown>;
}

export interface CompletionReportContact {
  name: string;
  phone: string | null;
  email: string | null;
  disposition: string | null;
  temperature: string | null;
  follow_up_action_type: string | null;
  follow_up_description: string | null;
  engagement_score: number | null;
}

export interface CompletionReportMetrics {
  overview: {
    total_enrolments: number;
    calls_attempted: number;
    calls_connected: number;
    connect_rate_pct: number;
    qualified_count: number;
    conversion_rate_pct: number;
    opted_out: number;
    callbacks_requested: number;
    pipeline_dollars: number;
  };
  funnel: { by_exit_status: Record<string, number>; active_count: number };
  call_outcomes: {
    by_disposition: Record<string, number>;
    by_goal_outcome: Record<string, number>;
    by_temperature: Record<string, number>;
    avg_engagement: number | null;
    total_call_cost: number;
  };
  kpi_scorecard: CompletionReportKpi[];
  captured_data: CompletionReportCapturedField[];
  priority_contacts: CompletionReportContact[];
  sample_size: number;
}

export interface CompletionReportNarrative {
  headline: string;
  executive_summary: string;
  kpi_assessment: string;
  what_worked: string;
  what_to_improve: string;
  recommended_next_steps: string;
}

export interface CompletionReportPayload {
  status: 'none' | 'pending' | 'generating' | 'ready' | 'failed';
  campaign?: CompletionReportCampaign | null;
  metrics?: CompletionReportMetrics | null;
  narrative?: CompletionReportNarrative | null;
  narrative_model?: string | null;
  generated_at?: string | null;
  error?: string | null;
}

// LT-627: CSVExportResponse envelope (mirrors backend schemas.CSVExportResponse).
export interface CampaignCSVExportResponse {
  success: boolean;
  filename: string;
  download_url: string;
  message: string;
  export_id: string;
}

interface CampaignKpiProgress {
  name: string;
  target_value: number;
  unit: string;
  current_value: number;
  computed: boolean;
  computed_at?: string;
}

interface EnrolmentRow {
  id: string;
  workspace_id: string;
  campaign_id: string;
  contact_id: string;
  contact_name?: string | null;
  contact_email?: string | null;
  joined_at: string;
  exit_status: string | null;
  exited_at: string | null;
  exit_reason: string | null;
  deal_id: string | null;
  manual_override_actor_user_id: string | null;
  manual_override_at: string | null;
  post_completion_campaign_id: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * Pull a human-readable error message out of an axios error against the
 * /campaigns endpoints. Backend returns structured 409 / 403 / 410 / 404
 * bodies — surface them verbatim where useful so transition rejection
 * toasts carry the server's reason ("from=active, to=paused, allowed=[...]").
 */
export function extractCampaignApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const anyErr = err as {
      response?: { status?: number; data?: { detail?: unknown } };
    };
    const detail = anyErr.response?.data?.detail;

    if (typeof detail === 'string') return detail;

    if (detail && typeof detail === 'object') {
      const obj = detail as Record<string, unknown>;

      if (obj.error === 'illegal_transition') {
        const allowed = Array.isArray(obj.allowed) ? obj.allowed.join(', ') : '';
        return `Cannot transition from "${obj.from}" to "${obj.to}"${
          allowed ? ` — allowed: ${allowed}` : ''
        }.`;
      }

      if (obj.error === 'locked_field') {
        const fields = Array.isArray(obj.fields) ? obj.fields.join(', ') : '';
        return `${fields} cannot be edited in status "${obj.status}".`;
      }

      if (obj.error === 'forbidden') {
        return typeof obj.reason === 'string' ? obj.reason : 'Forbidden.';
      }

      if (obj.error === 'completion_is_automatic') {
        return 'Completion is automatic — campaigns complete when every enrolment reaches a terminal exit.';
      }

      if (typeof obj.message === 'string') return obj.message;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
